import { useCustomerStore } from '@/stores/customerStore'
import { useDemographicStore } from '@/stores/demographicStore'
import { useFarmStore } from '@/stores/farmStore'
import { useFieldStore } from '@/stores/fieldStore'
import { useAgreementTermStore } from '@/stores/agreementTermStore'
import { useEconomicStore } from '@/stores/economicStore'
import { useUserStore } from '@/stores/userStore'
import { useHarvestStore } from '@/stores/harvestStore'
import { useAgriHarvestStore } from '@/stores/agriHarvestStore'
import { useAgriOrderStore } from '@/stores/agriOrderStore'
import { useAgriOrderProductStore } from '@/stores/agriOrderProductStore'
import { useTrainingCourseStore } from '@/stores/trainingCourseStore'
import { useTrainingScheduleStore } from '@/stores/trainingScheduleStore'
import { useTrainingAttendanceStore } from '@/stores/trainingAttendanceStore'
import { useAssessmentStore } from '@/stores/assessmentStore'
import { useAuthStore } from '@/stores/authStore.js'
import { useAgriProductStore } from '@/stores/agriProductStore.js'
import { useCropStore } from '@/stores/cropStore.js'
import { useSurveyResponseStore } from '@/stores/surveyResponseStore'
import { useSurveyResponseAnswerStore } from '@/stores/surveyResponseAnswerStore'
import { useAgriSubContractStore } from '@/stores/agriSubContractStore.js'
import { useConnectResource } from '@/composables/resources/useConnectResource.js'
import { useApiResource } from '@/composables/resources/useApiResource.js'
import { useAgriSubContractStepStore } from '@/stores/agriSubContractStepStore.js'
import { useAgriApplicationStore } from '@/stores/agriApplicationStore.js'
import { useAgriProductAgriApplicationStore } from '@/stores/agriProductAgriApplicationStore.js'
import { useSurveyStore } from '@/stores/surveyStore.js'
import { useSurveyQuestionStore } from '@/stores/surveyQuestionStore.js'
import { useDepotStore } from '@/stores/depotStore.js'
import { useBusinessStore } from '@/stores/businessStore.js'
import { useRecommendationStore } from '@/stores/recommendationStore.js'
import { computed, ref } from 'vue'
import Customer from '@/models/Customer.js'
import Demographic from '@/models/Demographic.js'
import Field from '@/models/Field.js'
import Assessment from '@/models/Assessment.js'
import Farm from '@/models/Farm.js'
import Economic from '@/models/Economic.js'
import Harvest from '@/models/Harvest.js'
import AgriHarvest from '@/models/AgriHarvest.js'
import AgriOrderProduct from '@/models/AgriOrderProduct.js'
import AgriOrder from '@/models/AgriOrder.js'
import AgreementTerm from '@/models/AgreementTerm.js'
import SurveyResponse from '@/models/SurveyResponse.js'
import SurveyResponseAnswer from '@/models/SurveyResponseAnswer.js'
import TrainingCourse from '@/models/TrainingCourse.js'
import TrainingSchedule from '@/models/TrainingSchedule.js'
import TrainingAttendance from '@/models/TrainingAttendance.js'
import AgriSubContract from '@/models/AgriSubContract.js'
import AgriSubContractStep from '@/models/AgriSubContractStep.js'
import AgriProduct from '@/models/AgriProduct.js'
import Crop from '@/models/Crop.js'
import AgriApplication from '@/models/AgriApplication.js'
import AgriProductApplication from '@/models/AgriProductAgriApplication.js'
import Survey from '@/models/Survey.js'
import SurveyQuestion from '@/models/SurveyQuestion.js'
import Depot from '@/models/Depot.js'
import Business from '@/models/Business.js'
import Recommendation from '@/models/Recommendation.js'
import { ModelRegistry } from '@/models/modelRegistry.js'
import { acknowledgeQueueState, offlineSupportAvailable, queuedRecordState } from '@/helpers/offlineSupport.js'
import { useDeviceIdentification } from '@/composables/useDeviceIdentification.js'
import { sessionEnded } from '@/helpers/sessionState.js'

const isSyncing = ref(false)

// The polling and the listeners watchSync starts, kept here rather than per caller, so that ending
// the session (helpers/session.js) can stop them from anywhere
const POLLING_INTERVAL_AMOUNT = 30000
let pollingInterval
let listenerController = null

// Shared by every caller (the polling, the Sync buttons): one delivery at a time, and one more after
// it when another was asked for meanwhile, so the same save is not sent twice at once. Requests have
// no timeout, so a delivery stuck on a weak signal for FLUSH_STUCK_MS no longer holds up the next
// (a record still being sent is not sent again: useSyncableModel's `sending`).
const FLUSH_STUCK_MS = 60 * 1000
let flushRun = null
let flushStartedAt = 0
let flushAgain = false

// When the device last came back online: a save the service worker holds waits from then (see
// useSyncableModel#releaseStaleQueued)
let onlineSince = Date.now()
if (typeof window !== 'undefined') window.addEventListener('online', () => { onlineSince = Date.now() })

export const useDataSync = () => {
  const authStore = useAuthStore()
  const customerStore = useCustomerStore()
  const demographicStore = useDemographicStore()
  const farmStore = useFarmStore()
  const fieldStore = useFieldStore()
  const economicStore = useEconomicStore()
  const harvestStore = useHarvestStore()
  const agriHarvestStore = useAgriHarvestStore()
  const agreementTermStore = useAgreementTermStore()
  const agriProductStore = useAgriProductStore()
  const agriOrderStore = useAgriOrderStore()
  const assessmentStore = useAssessmentStore()
  const cropStore = useCropStore()
  const surveyResponseStore = useSurveyResponseStore()
  const surveyResponseAnswerStore = useSurveyResponseAnswerStore()
  const trainingCourseStore = useTrainingCourseStore()
  const trainingScheduleStore = useTrainingScheduleStore()
  const trainingAttendanceStore = useTrainingAttendanceStore()
  const agriSubContractStore = useAgriSubContractStore()
  const agriSubContractStepStore = useAgriSubContractStepStore()
  const userStore = useUserStore()
  const agriOrderProductStore = useAgriOrderProductStore()
  const agriApplicationStore = useAgriApplicationStore()
  const agriProductAgriApplicationStore = useAgriProductAgriApplicationStore()
  const surveyStore = useSurveyStore()
  const surveyQuestionStore = useSurveyQuestionStore()
  const depotStore = useDepotStore()
  const businessStore = useBusinessStore()
  const recommendationStore = useRecommendationStore()

  let unsyncedRecordCount = computed(_calculateUnsyncedRecordCount)

  function watchSync() {
    listenerController = new AbortController()
    startPolling()

    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data.type === "recordSynced") _updateSyncedRecord(event.data.data)
      if (event.data.type === "syncing") {
        isSyncing.value = event.data.syncing
      }
    },
      { signal: listenerController.signal })

    window.addEventListener('online', () => flushPendingSaves(), { signal: listenerController.signal })
    flushPendingSaves()
  }

  function stopWatchSync() {
    stopPolling()
    listenerController?.abort()
    listenerController = null
  }

  function startPolling() {
    if (pollingInterval) return

    pollingInterval = setInterval(async () => {
      if (sessionEnded() || !userStore.user || !authStore.hasDeviceIdentifier()) return
      // Waited for only so long: changes from the server still arrive while a delivery is stuck
      await Promise.race([flushPendingSaves(), new Promise((resolve) => setTimeout(resolve, 20 * 1000))])
      await syncModels()
    }, POLLING_INTERVAL_AMOUNT)
  }

  // Delivers local saves that nothing else will: first lets each store settle the saves the service
  // worker holds (delivered, handed back, lost, or held too long), then resends the ones it is not
  // delivering. Safe to call offline.
  function flushPendingSaves() {
    if (flushRun && Date.now() - flushStartedAt < FLUSH_STUCK_MS) {
      flushAgain = true
      return flushRun
    }

    const run = _flushPendingSaves().finally(() => {
      if (flushRun !== run) return
      flushRun = null
      if (flushAgain) {
        flushAgain = false
        flushPendingSaves()
      }
    })
    flushRun = run
    flushStartedAt = Date.now()
    return run
  }

  async function _flushPendingSaves() {
    if (sessionEnded() || !userStore.user) return

    try {
      const stores = ModelRegistry.all()
        .map((model) => (typeof model.store === 'function' ? model.store() : null))
        .filter((store) => typeof store?.resendUnsent === 'function')

      if (!offlineSupportAvailable()) {
        stores.forEach((store) => store.reconcileQueued(null))
      }
      else {
        const asOf = Date.now()
        const deviceId = authStore.deviceIdentifier
        const queueState = await queuedRecordState()
        if (sessionEnded()) return

        if (queueState) {
          // Saves the previous version of the app queued from this partner's device
          const adoptable = new Set([...queueState.unqueued].filter((uuid) => queueState.handedBackDevices.get(uuid) === deviceId))
          // Only the handed-back saves a store took are forgotten by the worker; the rest stay with it
          const taken = new Set()
          stores.forEach((store) => {
            const takenHere = store.reconcileQueued(queueState.queued, queueState.unqueued, {
              asOf, reasons: queueState.reasons, delivered: queueState.delivered, adoptable, onlineSince
            })
            takenHere?.forEach((uuid) => taken.add(uuid))
          })

          // The server rejected this device, so the worker's next syncs would fail the same way
          let deviceKnown = false
          if (queueState.deviceUnknown) {
            deviceKnown = await useDeviceIdentification().reprovisionDevice()
              .catch((error) => console.warn('Could not provision this device again; will try again', error))
          }

          if (taken.size > 0 || queueState.delivered.size > 0 || queueState.deviceUnknown) {
            acknowledgeQueueState({ unqueued: taken, delivered: queueState.delivered, deviceKnown: deviceKnown === true })
          }
        }
        // The worker cannot say what it holds (busy, or a version that does not answer), so its saves
        // are left to it -- until they have waited too long
        else stores.forEach((store) => store.releaseStaleQueued({ onlineSince }))
      }

      for (const store of stores) {
        if (sessionEnded()) return
        await store.resendUnsent()
      }
    }
    catch (error) {
      console.warn('Could not deliver pending saves; will try again', error)
    }
  }

  function stopPolling() {
    if (!pollingInterval) return
    clearInterval(pollingInterval)
    pollingInterval = undefined
  }

  function triggerBackgroundSync() {
    // Registering a sync event is the only way to manually trigger the background sync.
    // We register the event with a specific tag so the event handler can determine where the event originated from
    // Without Background Sync (Safari, Firefox) the worker is asked directly.
    navigator.serviceWorker?.ready.then(registration => {
      if (!('sync' in registration)) {
        registration.active?.postMessage({ type: 'processQueue' })
        return
      }
      registration.sync.register('triggerBackgroundSync').then(() => {})
    });
    flushPendingSaves()
  }
  
  async function syncModels() {
    let connectResource = useConnectResource()
    let apiResource = useApiResource()

    let endpoint = "updates"
    let queryParams = { remote_device: { uuid: authStore.deviceIdentifier } }

    let connectResponse = connectResource.get(endpoint, queryParams)
    let apiResponse = apiResource.get(endpoint, queryParams)

    const results = await Promise.all([connectResponse, apiResponse])
    // What the server sent for a session that has since ended is not kept
    if (sessionEnded()) return

    for (let result of results) {
      const changedRecords = result.data

      if (Array.isArray(changedRecords.customers) && changedRecords.customers.length > 0) customerStore.mergeFromServer(changedRecords.customers)
      if (Array.isArray(changedRecords.demographics) && changedRecords.demographics.length > 0) demographicStore.mergeFromServer(changedRecords.demographics)
      if (Array.isArray(changedRecords.farms) && changedRecords.farms.length > 0) farmStore.mergeFromServer(changedRecords.farms)
      if (Array.isArray(changedRecords.fields) && changedRecords.fields.length > 0) fieldStore.mergeFromServer(changedRecords.fields)
      if (Array.isArray(changedRecords.economics) && changedRecords.economics.length > 0) economicStore.mergeFromServer(changedRecords.economics)
      if (Array.isArray(changedRecords.agri_harvests) && changedRecords.agri_harvests.length > 0) agriHarvestStore.mergeFromServer(changedRecords.agri_harvests)
      if (Array.isArray(changedRecords.harvests) && changedRecords.harvests.length > 0) harvestStore.mergeFromServer(changedRecords.harvests)
      if (Array.isArray(changedRecords.agri_orders) && changedRecords.agri_orders.length > 0) agriOrderStore.mergeFromServer(changedRecords.agri_orders)
      if (Array.isArray(changedRecords.agri_products) && changedRecords.agri_products.length > 0) agriProductStore.mergeFromServer(changedRecords.agri_products)
      if (Array.isArray(changedRecords.crops) && changedRecords.crops.length > 0) cropStore.mergeFromServer(changedRecords.crops)
      if (Array.isArray(changedRecords.training_courses) && changedRecords.training_courses.length > 0) trainingCourseStore.mergeFromServer(changedRecords.training_courses)
      if (Array.isArray(changedRecords.agri_sub_contracts) && changedRecords.agri_sub_contracts.length > 0) agriSubContractStore.mergeFromServer(changedRecords.agri_sub_contracts)
      if (Array.isArray(changedRecords.agri_sub_contract_steps) && changedRecords.agri_sub_contract_steps.length > 0) agriSubContractStepStore.mergeFromServer(changedRecords.agri_sub_contract_steps)
      if (Array.isArray(changedRecords.agri_applications) && changedRecords.agri_applications.length > 0) agriApplicationStore.mergeFromServer(changedRecords.agri_applications)
      if (Array.isArray(changedRecords.agri_product_applications) && changedRecords.agri_product_applications.length > 0) agriProductAgriApplicationStore.mergeFromServer(changedRecords.agri_product_applications)
      if (Array.isArray(changedRecords.surveys) && changedRecords.surveys.length > 0) surveyStore.mergeFromServer(changedRecords.surveys)
      if (Array.isArray(changedRecords.survey_questions) && changedRecords.survey_questions.length > 0) surveyQuestionStore.mergeFromServer(changedRecords.survey_questions)
      if (Array.isArray(changedRecords.survey_responses) && changedRecords.survey_responses.length > 0) surveyResponseStore.mergeFromServer(changedRecords.survey_responses)
      if (Array.isArray(changedRecords.survey_response_answers) && changedRecords.survey_response_answers.length > 0) surveyResponseAnswerStore.mergeFromServer(changedRecords.survey_response_answers)
      if (Array.isArray(changedRecords.depots) && changedRecords.depots.length > 0) depotStore.mergeFromServer(changedRecords.depots)
      if (Array.isArray(changedRecords.businesses) && changedRecords.businesses.length > 0) businessStore.mergeFromServer(changedRecords.businesses)
      if (Array.isArray(changedRecords.recommendations) && changedRecords.recommendations.length > 0) recommendationStore.mergeFromServer(changedRecords.recommendations)
    }

    // Farmers deleted on the server, sent only when there are some. Taken off after both answers are
    // merged, so neither brings a deleted farmer back.
    const deletedCustomers = results.flatMap((result) => {
      const uuids = result.data?.deletions?.customers
      return Array.isArray(uuids) ? uuids : []
    })
    if (deletedCustomers.length > 0) customerStore.removeDeleted(deletedCustomers)
  }

  function _calculateUnsyncedRecordCount() {
    let count = 0

    count += Customer.store().unsyncedCount()
    count += Demographic.store().unsyncedCount()
    count += Field.store().unsyncedCount()
    count += Farm.store().unsyncedCount()
    count += Economic.store().unsyncedCount()
    count += Assessment.store().unsyncedCount()
    count += Harvest.store().unsyncedCount()
    count += AgriHarvest.store().unsyncedCount()
    count += AgriOrder.store().unsyncedCount()
    count += AgriOrderProduct.store().unsyncedCount()
    count += AgreementTerm.store().unsyncedCount()
    count += SurveyResponse.store().unsyncedCount()
    count += SurveyResponseAnswer.store().unsyncedCount()
    count += TrainingCourse.store().unsyncedCount()
    count += TrainingSchedule.store().unsyncedCount()
    count += TrainingAttendance.store().unsyncedCount()
    count += AgriSubContract.store().unsyncedCount()
    count += AgriSubContractStep.store().unsyncedCount()
    count += AgriProduct.store().unsyncedCount()
    count += Crop.store().unsyncedCount()
    count += AgriApplication.store().unsyncedCount()
    count += AgriProductApplication.store().unsyncedCount()
    count += Survey.store().unsyncedCount()
    count += SurveyQuestion.store().unsyncedCount()
    count += Depot.store().unsyncedCount()
    count += Business.store().unsyncedCount()
    count += Recommendation.store().unsyncedCount()

    return count
  }

  function _updateSyncedRecord(recordData) {
    if (sessionEnded()) return
    let model = ModelRegistry.find(recordData.modelClass)
    // Settles the save only if the version delivered is the latest (useSyncableModel#recordDelivered)
    if (model) model.store().recordDelivered(recordData.modelUuid, recordData.sentAt ?? null)
  }

  return {
    watchSync, stopWatchSync, triggerBackgroundSync, syncModels, flushPendingSaves, unsyncedRecordCount, isSyncing
  }
}